import { clsx } from 'clsx';
import { Button } from '@/shared/ui/Button';
import { formatDate } from '@/shared/utils/format';
import type { PartnerRow } from '../api/communityApi';
import styles from './Community.module.css';

const STATUS_CLASS: Record<PartnerRow['status'], string> = {
  on_track: styles.pillSuccess,
  due_soon: styles.pillWarning,
  overdue: styles.pillError,
  planned: styles.pillInfo,
  new: styles.pillMuted,
};

export function NetworkTable({
  rows,
  selectedId,
  onSelect,
  onAdd,
}: {
  rows: PartnerRow[];
  selectedId: string | null;
  onSelect: (row: PartnerRow) => void;
  onAdd: () => void;
}) {
  return (
    <section className={styles.networkPanel} aria-label="My network">
      <div className={styles.networkHead}>
        <div>
          <div className={styles.networkTitle}>My network</div>
          <div className={styles.networkSub}>People you have chosen to stay close to. Joe reminds you when a touch is due. You set the rhythm.</div>
        </div>
        <Button variant="secondary" size="sm" onClick={onAdd}>
          + Add someone I know
        </Button>
      </div>
      <div className={clsx(styles.networkGrid, styles.networkHeaderRow)} aria-hidden>
        <div>Partner</div>
        <div>Last touch</div>
        <div className={styles.networkHide}>Rhythm</div>
        <div className={styles.networkHide}>Next</div>
        <div className={styles.networkHide}>Referrals (90d)</div>
        <div>Status</div>
      </div>
      {rows.length === 0 ? (
        <div className={styles.footnote}>Nobody yet. Plan a touch from Joe's picks, or add someone you already know.</div>
      ) : (
        rows.map((row) => (
          <button
            key={row.id}
            type="button"
            className={clsx(styles.networkGrid, styles.networkRow, selectedId === row.id && styles.networkRowSelected)}
            onClick={() => onSelect(row)}
          >
            <div>
              <div className={styles.partnerName}>{row.name}</div>
              <div className={styles.partnerRole}>{[row.role, row.org].filter(Boolean).join(' · ') || row.categoryLabel}</div>
            </div>
            <div className={styles.cellMuted}>
              {row.lastTouch ? `${row.lastTouch.label} · ${formatDate(row.lastTouch.date, 'MMM d')}` : row.nextPlanned ? `Planned: ${row.nextPlanned.label}` : 'None yet'}
            </div>
            <div className={clsx(styles.cellMuted, styles.networkHide)}>{row.rhythmLabel}</div>
            <div className={clsx(styles.cellMuted, styles.networkHide)}>
              {row.nextPlanned ? formatDate(row.nextPlanned.date, 'MMM d') : row.nextDue ? formatDate(row.nextDue, 'MMM d') : '-'}
            </div>
            <div className={clsx(styles.cellMuted, styles.networkHide)}>{row.referrals90d === null ? '-' : row.referrals90d}</div>
            <div>
              <span className={clsx(styles.pill, STATUS_CLASS[row.status])}>{row.statusLabel}</span>
            </div>
          </button>
        ))
      )}
      <div className={styles.footnote}>
        Referral counts are directional: monthly totals you confirm in Joe's referral check-in. We do not read your PMS, so no patient details are ever stored.
      </div>
    </section>
  );
}
